import {
  JSONRPCClient,
  JSONRPCServer,
  JSONRPCServerAndClient,
} from 'json-rpc-2.0';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { saveCredentials } from './tokenStorage';
import {
  isAuthFailure,
  isNewLogonError,
  isSessionTimeoutError,
  isConnectivityIssue,
  categorizeErrorMessage,
} from './sessionErrorUtils';

type LoginResponse = {
  accountDisabled: boolean;
  accountLocked: boolean;
  alreadyLoggedIn: boolean;
  changePassword: boolean;
  email: string;
  groups: string;
  logo: string;
  reason: string;
  sessionUuid: string;
  success: boolean;
  userName: string;
  userUuid: string;
};

export interface Session {
  request: (method: string, params: any) => PromiseLike<any>;
  serverAndClient: JSONRPCServerAndClient | undefined;
  webSocket: WebSocket | undefined;
  socketId: string;
  type: 'olSession' | 'dbSession' | 'replaySession';
}

type SessionType = 'olSession' | 'dbSession' | 'replaySession';

function dispatchCustomEvent(eventName: string, detail: any = null): void {
  const event = new CustomEvent(eventName, { detail: detail });
  window.dispatchEvent(event);
}

function dispatchWebSocketCloseEvent(websocket: WebSocket) {
  if (websocket.url.includes('olserver')) {
    dispatchCustomEvent('connectionClosed', 'olSession');
  } else if (websocket.url.includes('dbserver')) {
    dispatchCustomEvent('connectionClosed', 'dbSession');
  } else if (websocket.url.includes('replay')) {
    dispatchCustomEvent('connectionClosed', 'replaySession');
  } else {
    dispatchCustomEvent('connectionClosed');
  }
}

function getSessionTypeFromUrl(url: string): SessionType {
  if (url.includes('olserver')) {
    return 'olSession';
  } else if (url.includes('dbserver')) {
    return 'dbSession';
  } else if (url.includes('replay')) {
    return 'replaySession';
  }
  return 'olSession'; // default
}

function initializeWebSocketEventHandlers(
  serverwebSocket: WebSocket,
  serverAndClient: { receiveAndSend: (messageData: any) => void }
): void {
  serverwebSocket.onmessage = (event: MessageEvent) => {
    try {
      const messageData = JSON.parse(event.data.toString());

      // Check for session errors BEFORE passing through for immediate detection
      if (messageData.error) {
        const errorMessage = messageData.error.message || '';

        // Determine if this is an auth failure or connectivity issue
        const isAuthError = isAuthFailure(errorMessage) || isNewLogonError(errorMessage) || isSessionTimeoutError(errorMessage);

        if (isAuthError) {
          console.warn('Session error detected:', errorMessage);

          const sessionType = getSessionTypeFromUrl(serverwebSocket.url);
          const errorCategory = categorizeErrorMessage(errorMessage);

          // Auth failures are logged as errors
          Sentry.captureMessage(`Session auth error: ${errorMessage}`, {
            level: 'error',
            tags: {
              error_type: isAuthFailure(errorMessage)
                ? 'invalid_session_id'
                : isNewLogonError(errorMessage)
                ? 'new_logon_detected'
                : isSessionTimeoutError(errorMessage)
                ? 'session_timeout'
                : 'session_not_found',
              sessionType,
              error_category: errorCategory,
              error_severity: 'auth_failure',
            },
            extra: {
              error: messageData.error,
              url: serverwebSocket.url,
            },
          });

          // Dispatch the session error event immediately with error details
          dispatchCustomEvent('sessionError', {
            errorMessage,
            isNewLogon: isNewLogonError(errorMessage),
          });
          dispatchCustomEvent('sessionError', {
            errorMessage,
            isSessionTimeout: isSessionTimeoutError(errorMessage),
          });

          // Still pass through to handle the response properly
          serverAndClient.receiveAndSend(messageData);
          return;
        }

        const sessionType = getSessionTypeFromUrl(serverwebSocket.url);
        const errorCategory = categorizeErrorMessage(messageData.error.message || '');

        // Connectivity issues are logged as warnings (less alarming)
        if (isConnectivityIssue(errorMessage)) {
          Sentry.captureMessage(
            `WebSocket connectivity issue: ${messageData.error.message || 'Unknown error'}`,
            {
              level: 'warning',
              tags: {
                error_type: 'websocket_connectivity_issue',
                sessionType,
                error_category: errorCategory,
                error_severity: 'connectivity',
              },
              extra: {
                error: messageData.error,
                url: serverwebSocket.url,
                errorCode: messageData.error.code,
              },
            }
          );
        } else {
          // Other errors - capture as warning by default
          Sentry.captureMessage(
            `WebSocket error: ${messageData.error.message || 'Unknown error'}`,
            {
              level: 'warning',
              tags: {
                error_type: 'websocket_message_error',
                sessionType,
                error_category: errorCategory,
                error_severity: 'unknown',
              },
              extra: {
                error: messageData.error,
                url: serverwebSocket.url,
                errorCode: messageData.error.code,
              },
            }
          );
        }
      }

      // Handle special server-initiated messages
      if (messageData.method === 'update') {
        dispatchCustomEvent('eventNotification', messageData.params);
        return;
      }

      if (messageData.method === 'overlay') {
        dispatchCustomEvent('replayOverlay', messageData.params);
        return;
      }

      // Pass everything else through
      serverAndClient.receiveAndSend(messageData);
    } catch (e) {
      console.error('Failed to parse message data:', e);

      const sessionType = getSessionTypeFromUrl(serverwebSocket.url);

      Sentry.captureException(e, {
        tags: {
          error_type: 'websocket_parse_error',
          sessionType,
        },
        extra: {
          url: serverwebSocket.url,
          rawData: event.data?.toString?.().substring(0, 500),
        },
      });
    }
  };

  serverwebSocket.onclose = (closeEvent: CloseEvent) => {
    // Only log if it's an unexpected close
    if (!closeEvent.wasClean || closeEvent.code !== 1000) {
      console.warn(
        `WebSocket Closed unexpectedly with url ${serverwebSocket.url}:`,
        closeEvent
      );

      // Unexpected closes are connectivity issues - log as warning
      Sentry.captureMessage(
        `WebSocket closed unexpectedly: ${closeEvent.reason || 'No reason'}`,
        {
          level: 'warning',
          tags: {
            error_type: 'unexpected_close',
            close_code: closeEvent.code.toString(),
            error_severity: 'connectivity',
          },
          extra: {
            url: serverwebSocket.url,
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
          },
        }
      );
    }

    dispatchWebSocketCloseEvent(serverwebSocket);
  };
}

async function initializeWebSocket(
  basePath: string,
  allowFailure = false
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  const webSocket = await connectToWebSocket(basePath, allowFailure);

  if (!webSocket) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  const serverAndClient = new JSONRPCServerAndClient(
    new JSONRPCServer(),
    new JSONRPCClient((jsonRPCRequest) => {
      try {
        webSocket.send(JSON.stringify(jsonRPCRequest));
        return Promise.resolve();
      } catch (error) {
        console.error('Failed basePath:', basePath);
        console.error('Failed to send message:', error);
        if (!allowFailure) {
          const wsUrl = webSocket.url || ``;

          let errorMessage = 'Unknown error';
          if (error instanceof Error) {
            errorMessage = error.message;
          } else if (typeof error === 'string') {
            errorMessage = error;
          }

          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: wsUrl,
            error: errorMessage,
          });
        }
        return Promise.reject(error);
      }
    })
  );

  return { webSocket, serverAndClient };
}

export async function initializeOlWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let olServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    olServerBasePath = `${websocketProtocol}://${backendHostname}/ws/olserver`;
  } else {
    olServerBasePath = `${websocketProtocol}://${hostname}/ws/olserver`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    olServerBasePath
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeDbWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let dbServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    dbServerBasePath = `${websocketProtocol}://${backendHostname}/ws/dbserver`;
  } else {
    dbServerBasePath = `${websocketProtocol}://${hostname}/ws/dbserver`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    dbServerBasePath,
    true
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeReplayWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let replayServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    replayServerBasePath = `${websocketProtocol}://${backendHostname}/ws/replay`;
  } else {
    replayServerBasePath = `${websocketProtocol}://${hostname}/ws/replay`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    replayServerBasePath,
    true
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export interface Sessions {
  olSession: Session | undefined;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string | undefined;
}

export interface WebSockets {
  olServerAndClient: JSONRPCServerAndClient | undefined;
  olWebsocket: WebSocket | undefined;
  dbServerAndClient: JSONRPCServerAndClient | undefined;
  dbWebsocket: WebSocket | undefined;
  replayServerAndClient: JSONRPCServerAndClient | undefined;
  replayWebsocket: WebSocket | undefined;
}

export interface ValidatedSessions {
  olSession: Session;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string;
}

export interface User {
  email: string;
  groups: string[];
  username: string;
  userId: string;
}

export const getEmptySessions = (): Sessions => ({
  olSession: undefined,
  dbSession: undefined,
  replaySession: undefined,
  sessionId: undefined,
});

export const getEmptyWebsockets = (): WebSockets => ({
  olServerAndClient: undefined,
  olWebsocket: undefined,
  dbServerAndClient: undefined,
  dbWebsocket: undefined,
  replayServerAndClient: undefined,
  replayWebsocket: undefined,
});

function extractLoginData(loginResponse: LoginResponse) {
  const sessionId = loginResponse['sessionUuid'];
  const email = loginResponse['email'];
  const userId = loginResponse['userUuid'];
  const groups =
    typeof loginResponse['groups'] === 'string'
      ? loginResponse['groups'].split(',').map((item) => item.trim())
      : [];
  const username = loginResponse['userName'];
  let reason = loginResponse['reason'];
  const changePassword = loginResponse['changePassword'];
  const accountDisabled = loginResponse['accountDisabled'];
  const success = loginResponse['success'];

  if (accountDisabled) {
    reason = 'ACCOUNT_DISABLED';
  } else if (changePassword) {
    reason = 'CHANGE_PASSWORD';
  } else if (reason?.includes('Too many active sessions')) {
    reason = 'TOO_MANY_SESSIONS';
  }

  return {
    sessionId,
    email,
    groups,
    username,
    userId,
    reason,
    success,
    changePassword,
    accountDisabled,
  };
}

export function isWebSocketError(response: any): boolean {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    !response.success
  );
}

// Helper to extract error message from response
function getErrorMessage(response: any): string {
  const originalMessage =
    response.errorMessage ||
    response.errorMsg ||
    response.error?.message ||
    'Unknown WebSocket error';

  // Provide user-friendly messages for specific error types
  if (originalMessage.includes('Limit of jobs exceeded')) {
    const match = originalMessage.match(
      /Limit of jobs exceeded: (\d+) of (\d+)/
    );
    if (match) {
      const [, current, total] = match;
      return `Maximum number of reports reached (${current}/${total} reports).`;
    }
    return 'Maximum number of reports reached.';
  }

  if (
    originalMessage.includes('dbservercontroller.cpp') &&
    originalMessage.includes('internal error')
  ) {
    return 'An internal server error occurred. Please try again or contact your administrator if the problem persists.';
  }

  return originalMessage;
}

// Helper to determine error type from response
function getErrorType(response: any): string {
  // Check for error in the response.error structure (JSON-RPC errors)
  if (response.error) {
    const errorMessage = response.error.message || '';
    if (errorMessage.includes('Invalid session ID') || errorMessage.includes('invalid session') || errorMessage.includes('NewLogonDetected') || errorMessage.includes('SessionTimeout')) {
      return 'invalid_session_id';
    }
    if (errorMessage.includes('SessionNotFound')) {
      return 'session_not_found';
    }
    if (
      errorMessage.includes('Invalid') &&
      errorMessage.includes('Parameters')
    ) {
      return 'invalid_parameters';
    }
    // Add specific handling for database server job limit errors
    if (errorMessage.includes('Limit of jobs exceeded')) {
      return 'db_job_limit_exceeded';
    }
    if (
      errorMessage.includes('dbservercontroller.cpp') &&
      errorMessage.includes('internal error')
    ) {
      return 'db_server_internal_error';
    }
    return 'websocket_message_error';
  }

  // Check for error messages in other fields (for backward compatibility)
  const errorMessage =
    response.errorMessage || response.errorMsg || response.message || '';
  if (errorMessage.includes('Invalid session ID') || errorMessage.includes('invalid session') || errorMessage.includes('NewLogonDetected') || errorMessage.includes('SessionTimeout')) {
    return 'invalid_session_id';
  }
  if (errorMessage.includes('SessionNotFound')) {
    return 'session_not_found';
  }
  // Add specific handling for database server job limit errors in other fields
  if (errorMessage.includes('Limit of jobs exceeded')) {
    return 'db_job_limit_exceeded';
  }
  if (
    errorMessage.includes('dbservercontroller.cpp') &&
    errorMessage.includes('internal error')
  ) {
    return 'db_server_internal_error';
  }

  return 'websocket_response_error';
}

// Helper to capture WebSocket errors with full context
function captureWebSocketError(
  error: Error | any,
  response: any,
  context: {
    method: string;
    params: any;
    sessionId: string;
    socketId: string;
    type: SessionType;
  }
): void {
  const errorType = getErrorType(response);
  const errorMessage =
    error instanceof Error ? error.message : getErrorMessage(response);

  // Note: Session error is already dispatched in onmessage handler for immediate detection
  // This is kept for logging purposes only

  Sentry.captureException(
    error instanceof Error ? error : new Error(errorMessage),
    {
      tags: {
        sessionType: context.type,
        method: context.method,
        socketId: context.socketId,
        hasSessionId: context.sessionId !== '',
        error_type: errorType,
      },
      extra: {
        response,
        params: context.params,
        errorMessage,
        sessionId: context.sessionId || 'no-session-id',
        // Include any additional error details from the response
        ...(response.error && { error: response.error }),
        ...(response.errorCode && { errorCode: response.errorCode }),
        ...(response.errorDetails && { errorDetails: response.errorDetails }),
        ...(response.stackTrace && { stackTrace: response.stackTrace }),
      },
      fingerprint: [
        'websocket-error',
        context.type,
        context.method,
        errorType,
        response.errorCode || 'no-error-code',
      ],
    }
  );
}

// Helper to build request parameters
function buildRequestParams(params: any, sessionId: string): any {
  if (sessionId === '') {
    return params;
  }

  return {
    ...params,
    sessionUuid: sessionId,
  };
}

export function createSession(
  serverAndClient: JSONRPCServerAndClient | undefined,
  webSocket: WebSocket | undefined,
  sessionId: string,
  type: SessionType
): Session | undefined {
  if (!serverAndClient) {
    return undefined;
  }

  const socketId = uuidv4();

  return {
    async request(method, params) {
      const finalParams = buildRequestParams(params, sessionId);
      const context = {
        method,
        params: finalParams,
        sessionId,
        socketId,
        type,
      };

      try {
        const response = await serverAndClient.request(method, finalParams);

        // Check if the response indicates an error
        if (isWebSocketError(response)) {
          const error = new Error(getErrorMessage(response));
          captureWebSocketError(error, response, context);
          throw error;
        }

        // Also check for JSON-RPC errors
        if (response && response.error) {
          const error = new Error(response.error.message || 'WebSocket error');
          captureWebSocketError(error, response, context);
          throw error;
        }

        return response;
      } catch (error) {
        // If error is already an Error object and not from our error handling above
        if (
          error instanceof Error &&
          !error.message.includes('WebSocket error') &&
          !error.message.includes('Unknown WebSocket error')
        ) {
          // This is likely a network error or timeout
          Sentry.captureException(error, {
            tags: {
              sessionType: type,
              method: method,
              socketId: socketId,
              hasSessionId: sessionId !== '',
              error_type: 'websocket_network_error',
            },
            extra: {
              params: finalParams,
              sessionId: sessionId || 'no-session-id',
              originalError: error.message,
              stack: error.stack,
            },
            fingerprint: [
              'websocket-network-error',
              type,
              method,
              error.name || 'Error',
            ],
          });
        }

        throw error;
      }
    },
    type,
    socketId,
    serverAndClient,
    webSocket,
  };
}

export async function initializeWebsockets() {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const { webSocket: olWebsocket, serverAndClient: olServerAndClient } =
    await initializeOlWebSocket(hostname, protocol);
  const { webSocket: dbWebsocket, serverAndClient: dbServerAndClient } =
    await initializeDbWebSocket(hostname, protocol);
  const { webSocket: replayWebsocket, serverAndClient: replayServerAndClient } =
    await initializeReplayWebSocket(hostname, protocol);

  return {
    olServerAndClient,
    olWebsocket,
    dbServerAndClient,
    dbWebsocket,
    replayServerAndClient,
    replayWebsocket,
  };
}

export type LoginErrorReason =
  | 'CONNECTION_ERROR'
  | 'INVALID_CREDENTIALS'
  | 'MISSING_SESSION'
  | 'MISSING_TOTP_CODE'
  | 'INVALID_TOTP_CODE'
  | 'MFA_REQUIRED'
  | 'CHANGE_PASSWORD'
  | 'ACCOUNT_DISABLED'
  | 'TOO_MANY_SESSIONS';

function getEmptyResponse(webSockets: WebSockets, reason: LoginErrorReason) {
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    '',
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.olServerAndClient,
    webSockets.dbWebsocket,
    '',
    'dbSession'
  )!;
  const replaySession = createSession(
    webSockets.olServerAndClient,
    webSockets.replayWebsocket,
    '',
    'replaySession'
  )!;

  return {
    sessions: {
      olSession: olSession,
      dbSession: dbSession,
      replaySession: replaySession,
      sessionId: undefined,
    },
    user: {
      email: '',
      groups: [],
      username: '',
      userId: '',
    },
    reason: reason,
  };
}

export async function createClientWithCredentials(
  credentials: {
    username: string;
    password: string;
  },
  webSockets: WebSockets,
  totpCode?: number,
  timeZone?: string
): Promise<{ sessions: Sessions; user: User; reason?: LoginErrorReason }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'CONNECTION_ERROR');
  }

  const basePayload = {
    userName: credentials.username,
    password: credentials.password,
    serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    locale: 'en_US',
    timeZone: timeZone ? timeZone : '',
    defaultTimeZone: 'UTC',
    stealSession: true,
    sessionUuid: '',
    applicationName: 'marinepulse',
  };

  const payload = totpCode ? { ...basePayload, totpCode } : basePayload;
  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('login', payload);
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  if (!success && reason) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  } else if (!success) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  // Save credentials to localStorage for automatic re-login on refresh
  saveCredentials({
    username: credentials.username,
    password: credentials.password,
  });

  // All the sessions here use the same session id, which is maintained by the backend
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession'
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession'
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

export async function createClientWithAzure(
  idToken: string,
  accessToken: string,
  webSockets: WebSockets,
  timeZone?: string
): Promise<{ sessions: Sessions; user: User }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('loginAzureAD', {
      idToken: idToken,
      accessToken: accessToken,
      serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
      locale: 'en_US',
      timeZone: timeZone ? timeZone : '',
      defaultTimeZone: 'UTC',
      stealSession: true,
      sessionUuid: '',
      applicationName: 'marinepulse',
    });
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  // All the sessions here use the same session id, which is maintained by the backend
  if (!success) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  }

  // Note: We don't save Azure credentials as they use tokens that expire
  // Azure users will need to re-authenticate on page refresh

  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession'
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession'
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

function connectToWebSocket(
  url: string,
  allowFailure: boolean
): Promise<WebSocket | undefined> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(url);
    let isResolved = false;

    // Add timeout for WebSocket connection (especially important for optional connections like replay/dbserver)
    const timeout = allowFailure ? 5000 : 15000; // 5s for optional, 15s for required
    const timeoutId = setTimeout(() => {
      if (!isResolved) {
        console.warn(`WebSocket connection timeout for ${url}`);
        isResolved = true;

        // Close the socket if still connecting
        if (
          socket.readyState === WebSocket.CONNECTING ||
          socket.readyState === WebSocket.OPEN
        ) {
          socket.close();
        }

        if (allowFailure) {
          // For optional connections (replay/dbserver), just resolve with undefined
          resolve(undefined);
        } else {
          // For required connections (olserver), reject
          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: url,
            error: 'WebSocket connection timeout',
          });
          reject(new Error(`WebSocket connection timeout for ${url}`));
        }
      }
    }, timeout);

    socket.onopen = () => {
      if (!isResolved && socket.readyState === WebSocket.OPEN) {
        isResolved = true;
        clearTimeout(timeoutId);
        resolve(socket);
      }
    };

    socket.onerror = (event) => {
      console.error('WebSocket error observed:', event);
      if (!isResolved) {
        clearTimeout(timeoutId);
        if (!allowFailure) {
          isResolved = true;
          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: socket.url,
            error: 'WebSocket connection error',
          });
          reject(new Error('Failed to open WebSocket connection.'));
        }
        // For allowFailure=true, let onclose handle it or timeout will catch it
      }
    };

    socket.onclose = (event) => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timeoutId);

        if (!event.wasClean && !allowFailure) {
          console.error('WebSocket closed unexpectedly:', event);

          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: socket.url,
            error: 'WebSocket connection closed unexpectedly',
            code: event?.code,
            reason: event?.reason,
          });

          dispatchWebSocketCloseEvent(socket);
          reject(new Error('WebSocket was closed uncleanly.'));
        } else {
          console.warn('WebSocket closed', event);
          resolve(undefined);
        }
      }
    };
  });
}
